/**
 * The review-queue closed sets, split out of `./review-queue.ts` (#1805 A3b).
 *
 * Nothing here reaches a transport. That is the whole point: the cell
 * projections that read these maps now live in `features/admin/widgets.tsx`,
 * and `src/features/widgets.ts` is evaluated by `src/test-setup.ts` for EVERY
 * suite, ahead of any `vi.mock`. A widget that reached `./review-queue.ts` for
 * a badge table would drag `@/lib/api` — and its module-level `axios.create` —
 * into every suite before a test could replace it (that cost 19 red tests in
 * slice A1).
 *
 * `./review-queue.ts` re-exports every member, so the
 * `@/services/review-queue` import path is unchanged for existing callers.
 * The `satisfies` clauses still pin the values to the generated wire unions,
 * so a backend rename fails compilation here exactly as it did before.
 */
import type { components } from '@/types/api.generated';

type ReviewQueueItem = components['schemas']['ReviewQueueItem'];

/**
 * Closed enum mirroring the backend `ReviewQueueItem.kind` union
 * (`backend/src/contexts/review/domain/kinds.ts`). The `satisfies` clause pins
 * every value to the generated union so a backend rename fails compilation
 * here rather than silently rendering an unknown badge.
 */
export const REVIEW_QUEUE_KIND = {
  POST_CUTOFF_ORDER_CHANGE: 'post_cutoff_order_change',
  POST_DISPATCH_CANCELLATION: 'post_dispatch_cancellation',
  DISPATCH_FAILED: 'dispatch_failed',
} as const satisfies Record<string, ReviewQueueItem['kind']>;
export type ReviewQueueKind = (typeof REVIEW_QUEUE_KIND)[keyof typeof REVIEW_QUEUE_KIND];

export const REVIEW_QUEUE_STATUS = {
  OPEN: 'open',
  RESOLVED: 'resolved',
} as const satisfies Record<string, ReviewQueueItem['status']>;
export type ReviewQueueStatus = (typeof REVIEW_QUEUE_STATUS)[keyof typeof REVIEW_QUEUE_STATUS];

/**
 * What the LIST QUERY accepts, mirroring the backend `REVIEW_STATUS_FILTER`
 * (#1998). A strict superset of the row statuses above: `all` is a view over
 * the rows, not a state a row can be in — which is why it is a second closed
 * set rather than a third member of the first one (`REVIEW_QUEUE_STATUS` is
 * pinned by `satisfies` to the values `ReviewQueueItem['status']` admits, and
 * `all` is not one of them).
 */
export const REVIEW_QUEUE_STATUS_FILTER = {
  OPEN: REVIEW_QUEUE_STATUS.OPEN,
  RESOLVED: REVIEW_QUEUE_STATUS.RESOLVED,
  /** Every row whatever its status — the history ledger. */
  ALL: 'all',
} as const;
export type ReviewQueueStatusFilter =
  (typeof REVIEW_QUEUE_STATUS_FILTER)[keyof typeof REVIEW_QUEUE_STATUS_FILTER];

/**
 * Closed set of `post_dispatch_cancellation` causes, mirroring the backend
 * `POST_DISPATCH_CANCELLATION_CAUSE` (`backend/src/contexts/review/domain/
 * kinds.ts`). `kind` is a deliberately WIDE bucket — the operator workflow is
 * identical across these causes, only the prompt differs — so the discriminator
 * lives on the row's `cause` column (`ReviewQueueItem.cause`, typed `string |
 * null` on the wire because the OpenAPI schema does not narrow it). The UI
 * derives its cause-badge labels + summary fragments from this one map so a
 * cause string is never a scattered magic literal; an out-of-set cause value
 * falls back to the kind label (drift is visible via console.warn, not a crash).
 */
export const POST_DISPATCH_CANCELLATION_CAUSE = {
  SHOPIFY_CANCELLATION_AFTER_DISPATCH: 'shopify_cancellation_after_dispatch',
  ORDER_REFUNDED_AFTER_DISPATCH: 'order_refunded_after_dispatch',
  QUANTITY_CHANGED_AFTER_DISPATCH: 'quantity_changed_after_dispatch',
  ADDRESS_CHANGED_AFTER_DISPATCH: 'address_changed_after_dispatch',
} as const;
export type PostDispatchCancellationCause =
  (typeof POST_DISPATCH_CANCELLATION_CAUSE)[keyof typeof POST_DISPATCH_CANCELLATION_CAUSE];

/**
 * Closed set of what changed on a `post_cutoff_order_change` row
 * (`details_json.change_types`, at least one), mirroring the backend
 * `POST_CUTOFF_CHANGE_TYPE` (`backend/src/contexts/review/domain/kinds.ts`). The phone inbox
 * (#2487) maps each to its label; an out-of-set value is dropped.
 */
export const POST_CUTOFF_CHANGE_TYPE = {
  ADDRESS: 'address',
  QUANTITY: 'quantity',
  CANCEL: 'cancel',
  OTHER: 'other',
} as const;
export type PostCutoffChangeType =
  (typeof POST_CUTOFF_CHANGE_TYPE)[keyof typeof POST_CUTOFF_CHANGE_TYPE];

/**
 * Closed set of provider-error kinds carried on `dispatch_failed`
 * details, mirroring the backend `REVIEW_ERROR_KIND`
 * (`backend/src/contexts/review/domain/kinds.ts`). The UI localizes the token
 * inside rebuilt summaries; a drifted token renders raw (visible, not hidden).
 */
export const REVIEW_ERROR_KIND = {
  PROVIDER_REJECTED: 'provider_rejected',
  PROVIDER_UNREACHABLE: 'provider_unreachable',
  UNEXPECTED: 'unexpected',
} as const;
export type ReviewErrorKind = (typeof REVIEW_ERROR_KIND)[keyof typeof REVIEW_ERROR_KIND];
